"use client";
import { FormData } from "@/getData/getFormsData";
import { HighlightsFormType } from "@/modules/Form/createFormStructure";
import { ModalType } from "@/modules/Insta/Modal/Modal";
import { InstagramModalState } from "@/modules/Instagram/Modal/Modal";
import { OptionsBookingType } from "@/types/OptionType";
import { usePathname } from "next/navigation";
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";

export type ActivePopupContent = {
  type: "accordion";
  categories: {
    name: string;
    elements: {
      title: string;
      content: string;
    }[];
  }[];
} | null;

type BookingModal = { open: boolean };
export interface IContextType {
  openMenu: boolean;
  setOpenMenu: Dispatch<SetStateAction<boolean>>;
  openPopup: boolean;
  setopenPopup: Dispatch<SetStateAction<boolean>>;
  activePopupContent: ActivePopupContent;
  setActivePopupContent: Dispatch<SetStateAction<ActivePopupContent>>;
  instagramModal: ModalType | null;
  setInstagramModal: Dispatch<SetStateAction<ModalType | null>>;
  instagramPostModal: InstagramModalState | null;
  setInstagramPostModal: Dispatch<SetStateAction<InstagramModalState | null>>;
  bookingModal: BookingModal;
  setBookingModal: Dispatch<SetStateAction<BookingModal>>;
  globalFormData: FormData[];
  friendlyCaptchaApplicationkey: string;
  highlights: HighlightsFormType;
  defaultHightlightSettings: Pick<
    OptionsBookingType,
    "highlights_choice_type" | "highlights_categories" | "highlights_choices"
  >;
}

const AppContext = createContext<IContextType | null>(null);

export const AppProvider = ({
  children,
  globalFormData,
  friendlyCaptchaApplicationkey,
  highlights,
  defaultHightlightSettings,
}: {
  children: React.ReactNode;
  globalFormData: FormData[];
  highlights: HighlightsFormType;
  friendlyCaptchaApplicationkey: string;
  defaultHightlightSettings: Pick<
    OptionsBookingType,
    "highlights_choice_type" | "highlights_categories" | "highlights_choices"
  >;
}) => {
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [bookingModal, setBookingModal] = useState<BookingModal>({
    open: false,
  });
  const [openPopup, setopenPopup] = useState<boolean>(false);
  const [activePopupContent, setActivePopupContent] =
    useState<ActivePopupContent>(null);
  const [instagramModal, setInstagramModal] = useState<ModalType | null>(null);
  const [instagramPostModal, setInstagramPostModal] =
    useState<InstagramModalState | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    setOpenMenu(false);
    setopenPopup(false);
    setActivePopupContent(null);
  }, [pathname]);

  return (
    <AppContext.Provider
      value={{
        openMenu,
        setOpenMenu,
        openPopup,
        setopenPopup,
        activePopupContent,
        setActivePopupContent,
        instagramModal,
        setInstagramModal,
        instagramPostModal,
        setInstagramPostModal,
        bookingModal,
        setBookingModal,
        globalFormData,
        friendlyCaptchaApplicationkey,
        highlights,
        defaultHightlightSettings,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): IContextType => {
  const context = useContext<IContextType | null>(AppContext);

  if (context === undefined || context === null) {
    throw new Error("Context must be used within a Provider");
  }
  return context;
};
